import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { CheckCircle2, Clock, Infinity as InfinityIcon, ShieldOff, HelpCircle } from "lucide-react";

const STATUS_META = {
  active: { color: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40", label: "Ativo" },
  pending: { color: "bg-yellow-500/20 text-yellow-300 border-yellow-500/40", label: "Aguardando liberação do Mentor" },
  blocked: { color: "bg-red-500/20 text-red-300 border-red-500/40", label: "Bloqueado" },
  expired: { color: "bg-orange-500/20 text-orange-300 border-orange-500/40", label: "Expirado" },
};

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("pt-BR"); } catch { return "—"; }
}

export default function StudentAccessCard() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get("/api/memberships/me");
        if (alive) setItems(res.data?.memberships || []);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("[student-access] load failed", err?.message);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  if (loading) {
    return (
      <div className="bg-[#14161C] border border-[#282D3D] rounded-xl p-5 text-xs text-gray-400">
        Carregando acesso...
      </div>
    );
  }

  if (!items.length) {
    return (
      <div className="bg-[#14161C] border border-[#282D3D] rounded-xl p-5 flex items-start gap-3">
        <HelpCircle className="w-5 h-5 text-yellow-400 shrink-0 mt-0.5" />
        <div className="text-xs text-gray-300">
          Você ainda não está vinculado a uma mentoria. Solicite um convite ao seu mentor
          para começar sua jornada.
        </div>
      </div>
    );
  }

  return (
    <div data-testid="student-access-card" className="bg-[#14161C] border border-[#282D3D] rounded-xl p-5 space-y-4">
      <h3 className="text-base font-bold text-white flex items-center gap-2">
        <CheckCircle2 className="w-5 h-5 text-[#FF6B00]" />
        Meu Acesso à Mentoria
      </h3>
      <div className="space-y-3">
        {items.map((m) => {
          const meta = STATUS_META[m.effective_status] || STATUS_META.pending;
          return (
            <div
              key={m.id}
              data-testid={`student-membership-${m.id}`}
              className="bg-[#1A1D26] border border-[#282D3D] rounded-lg p-4 space-y-2"
            >
              <div className="flex items-center justify-between gap-2">
                <div className="text-sm font-bold text-white">{m.mentorship_name || "Mentoria"}</div>
                <span className={`text-[11px] px-2.5 py-1 rounded-full font-bold border ${meta.color}`}>{meta.label}</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
                <div className="flex items-center gap-1.5 text-gray-300">
                  {m.is_lifetime ? (
                    <>
                      <InfinityIcon className="w-3.5 h-3.5 text-emerald-400" />
                      <span>Acesso <strong className="text-white">Vitalício</strong></span>
                    </>
                  ) : (
                    <>
                      <Clock className="w-3.5 h-3.5 text-[#FF6B00]" />
                      <span>Duração: <strong className="text-white">{m.access_duration_days ? `${m.access_duration_days} dias` : "—"}</strong></span>
                    </>
                  )}
                </div>
                <div className="text-gray-300">
                  Início: <strong className="text-white">{fmtDate(m.access_started_at)}</strong>
                </div>
                <div className="text-gray-300">
                  {m.is_lifetime ? (
                    <span>Vencimento: <strong className="text-white">nunca</strong></span>
                  ) : (
                    <span>Vencimento: <strong className="text-white">{fmtDate(m.access_expires_at)}</strong></span>
                  )}
                </div>
              </div>
              {!m.is_lifetime && m.days_remaining !== null && m.days_remaining !== undefined && m.effective_status === "active" && (
                <div className="text-[11px] text-gray-400">
                  Restam <strong className="text-white">{m.days_remaining}</strong> dias de acesso.
                </div>
              )}
              {m.effective_status === "pending" && (
                <div className="text-[11px] text-yellow-200/80">
                  Aguarde a liberação do seu mentor. Você receberá acesso completo assim que ele aprovar sua matrícula.
                </div>
              )}
              {m.effective_status === "expired" && (
                <div className="text-[11px] text-orange-300/90">
                  Seu acesso expirou. Fale com seu mentor para renovar.
                </div>
              )}
              {m.effective_status === "blocked" && (
                <div className="text-[11px] text-red-300/90 flex items-center gap-1.5">
                  <ShieldOff className="w-3.5 h-3.5" /> Acesso bloqueado pelo mentor.
                </div>
              )}
            </div>
          );
        })}
      </div>
      <p className="text-[10px] text-center text-gray-500">
        A duração e renovação do seu acesso são gerenciadas manualmente pelo seu mentor.
      </p>
    </div>
  );
}
